import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Full-screen maintenance overlay.
 * Blocks all interaction while maintenance is active.
 * Shows a countdown and the admin's custom message.
 * Auto-dismisses when the maintenance window ends.
 */
function MaintenanceOverlay({ message, endsAt }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!endsAt) return;

    const update = () => {
      const now = new Date();
      const diff = new Date(endsAt).getTime() - now.getTime();

      if (diff <= 0) {
        setRemaining('');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setRemaining(`${minutes}m ${seconds}s`);
      } else {
        setRemaining(`${seconds}s`);
      }
    };

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endsAt]);

  const overlay = (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'rgba(15, 23, 42, 0.85)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      animation: 'maintenanceFadeIn 400ms ease-out',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '460px',
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
        padding: '40px 32px',
        textAlign: 'center',
        animation: 'maintenanceSlideIn 500ms ease-out',
      }}>
        {/* Animated gear icon */}
        <div style={{
          width: '72px',
          height: '72px',
          margin: '0 auto 24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'maintenancePulse 2s ease-in-out infinite',
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 12px',
          fontSize: '22px',
          fontWeight: '700',
          color: '#0f172a',
        }}>
          {t('maintenance.overlayTitle')}
        </h2>

        {/* Admin message */}
        <div style={{
          padding: '16px 20px',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
        }}>
          <p style={{
            margin: 0,
            fontSize: '15px',
            lineHeight: '1.6',
            color: '#334155',
          }}>
            {message}
          </p>
        </div>

        {/* Countdown */}
        {remaining && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '24px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {t('maintenance.endsIn', { time: remaining })}
          </div>
        )}

        {/* Subtitle */}
        <p style={{
          marginTop: '20px',
          fontSize: '13px',
          color: '#94a3b8',
          lineHeight: '1.5',
        }}>
          {t('maintenance.overlaySubtitle')}
        </p>
      </div>

      <style>{`
        @keyframes maintenanceFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes maintenanceSlideIn {
          from { transform: translateY(20px) scale(0.97); opacity: 0; }
          to   { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes maintenancePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4); }
          50%      { transform: scale(1.05); box-shadow: 0 0 0 12px rgba(102, 126, 234, 0); }
        }
      `}</style>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default MaintenanceOverlay;
